/**
 * HERE Maps Geocoding & Search — thin REST client for the Discover API.
 *
 * Why HERE (and not Google Places):
 *   30k req/month free covers the backoffice volume by 30-60×, no SDK
 *   bundle weight (REST via native fetch), simpler 1-call shape (no
 *   Autocomplete + Place Details two-step dance).
 *
 * Why no SDK:
 *   `@here/maps-api-for-javascript` exists but only adds wrappers around
 *   what we'd hand-roll anyway, and pulls in unrelated features
 *   (interactive maps, geofencing, etc.). Native fetch + AbortController
 *   gives us full control over debounce / cancel without fighting an SDK.
 *
 * Italy-only by design:
 *   `in=countryCode:ITA` keeps results relevant to our domain (broker
 *   clients are residents/companies in Italy) and reduces noise from
 *   matching street names abroad. Removing the filter is a 1-line
 *   change when we expand internationally.
 *
 * Province code limitation (HERE quirk #1):
 *   HERE for IT exposes the provincia as full name in `address.county`
 *   (e.g. "Roma", "Milano"), NOT the 2-letter sigla (RM, MI). The
 *   `address.countyCode` field exists but is often empty for IT data.
 *   We handle both: try `countyCode` first (when it IS a 2-letter sigla),
 *   then fall back to mapping the full `county` name through the
 *   ITALIAN_PROVINCE_BY_NAME lookup. Anything we can't map cleanly is
 *   left `undefined` — manual fallback rule (the user types it).
 *
 * Country code conversion (HERE quirk #2):
 *   HERE returns `countryCode` as ISO 3166-1 alpha-3 ("ITA"), but the
 *   rest of the app + the BE write schema use alpha-2 ("IT"). We
 *   convert via ISO3_TO_ISO2 so consumers always see the alpha-2 form.
 *
 * Empty-string discipline (HERE quirk #3):
 *   HERE inconsistently returns missing fields as either absent keys
 *   OR empty strings (e.g. `"county": ""`). The `cleanString` helper
 *   collapses both into `undefined` so consumers can rely on a
 *   `string | undefined` contract — no need to also guard against
 *   `""` falsy values.
 */

const DISCOVER_ENDPOINT = 'https://discover.search.hereapi.com/v1/discover';

/**
 * Geographic bias point for the Discover API. The `at` parameter is
 * required; we use Italy's geographic centroid (Rieti area) so that
 * results are biased toward IT addresses without skewing toward any
 * specific city. Distance scoring still works — Roma queries return
 * Roma results, Milano queries return Milano results.
 */
const ITALY_CENTROID = '42.8333,12.8333';

export interface NormalizedAddress {
  /**
   * Street + house number joined ("Via Roma 12"), as suitable for a
   * single "Via e civico" form field. `undefined` when HERE returned
   * no street name — caller treats as "no pre-fill, user types
   * manually". The dedicated `houseNumber` atom is also exposed below
   * so a future form with a separate civico cell can use it directly.
   */
  street: string | undefined;
  /** Raw civic/house number ("12"), trimmed. `undefined` when missing. */
  houseNumber: string | undefined;
  /** City / comune name, trimmed. `undefined` when missing. */
  city: string | undefined;
  /** Italian CAP, trimmed. Format validation belongs to the form, not here. */
  postalCode: string | undefined;
  /**
   * Provincia 2-letter sigla, uppercase ("RM", "MI"). `undefined`
   * when HERE didn't surface a code AND the full county name didn't
   * match the ITALIAN_PROVINCE_BY_NAME table — never invent.
   */
  province: string | undefined;
  /**
   * ISO 3166-1 alpha-2 country code, uppercase ("IT"). HERE returns
   * alpha-3 ("ITA") which we convert. `undefined` when the country
   * code can't be normalized to a 2-letter form.
   */
  country: string | undefined;
  /** Display label for the dropdown row (HERE's `address.label`). */
  label: string;
  /** Stable HERE id for this result — used as React key. */
  id: string;
}

interface HereDiscoverResponse {
  items?: ReadonlyArray<HereDiscoverItem>;
}

interface HereDiscoverItem {
  title: string;
  id: string;
  resultType: string;
  address?: {
    label?: string;
    countryCode?: string;
    state?: string;
    stateCode?: string;
    county?: string;
    countyCode?: string;
    city?: string;
    district?: string;
    street?: string;
    postalCode?: string;
    houseNumber?: string;
  };
}

/**
 * Form shape with a single combined "Via e civico" field. Used by the
 * multi-address forms in the backoffice (client + org Indirizzi tab).
 * Five canonical atoms: street (combined civic), postalCode, city,
 * province (2-letter sigla), country (ISO2). Consumers whose form uses
 * different keys (e.g. `zip` instead of `postalCode`) cannot use the
 * helper directly and should keep an inline merge.
 */
export interface HereAddressFormFields {
  street: string;
  postalCode: string;
  city: string;
  province: string;
  country: string;
}

/**
 * Map a HERE `NormalizedAddress` over the canonical 5-atom subset of a
 * form, preserving any value the user already typed manually when HERE
 * did not surface that field. Pure function — returns a new object with
 * only the 5 atoms; the consumer spreads it into the rest of its form
 * state.
 *
 * Consumers (post 2026-04-29 rename from `mergeHereAddressIntoFormFields`):
 *   - features/organizations/.../OrganizationIndirizziTab
 *   - features/intermediary-onboarding/.../CreateIntermediaryOnboardingDialog
 *   - (NOT clients/.../ClientIndirizziTab — it uses different field keys
 *     `zip` vs `postalCode` and stays on its inline merge)
 */
export function mapHereAddressToFormAddress<T extends HereAddressFormFields>(
  addr: NormalizedAddress,
  form: T,
): HereAddressFormFields {
  return {
    street: addr.street || form.street,
    postalCode: addr.postalCode || form.postalCode,
    city: addr.city || form.city,
    province: addr.province || form.province,
    country: addr.country || form.country,
  };
}

/**
 * @deprecated Use `mapHereAddressToFormAddress` instead. Kept as alias to
 * avoid churn on existing consumers; no behavioural change.
 */
export const mergeHereAddressIntoFormFields = mapHereAddressToFormAddress;

/**
 * Split a combined "Via e civico" string into street name + civic
 * number atoms — needed for backends that store them separately
 * (`organization_addresses` keeps `street` and `streetNumber` as
 * separate non-null columns).
 *
 * Walk the tokens from the end backwards: the FIRST token (closest to
 * the end) that starts with a digit is the civic number. Anything
 * after it (Italian "bis", "ter", "interno", etc.) is appended to the
 * civic. Anything before becomes the street name. If no
 * digit-starting token is found, the entire string is the street name
 * and `streetNumber` is empty — the caller validates this case.
 *
 * Examples:
 *   "Via Roma 12"          → { street: "Via Roma",     streetNumber: "12" }
 *   "Corso Italia 12/A"    → { street: "Corso Italia", streetNumber: "12/A" }
 *   "Piazza Carlo 8 bis"   → { street: "Piazza Carlo", streetNumber: "8 bis" }
 *   "Via 4 Novembre 12"    → { street: "Via 4 Novembre", streetNumber: "12" }
 *   "Via 4 Novembre"       → { street: "Via 4 Novembre", streetNumber: "" }
 *   "Via Roma"             → { street: "Via Roma",     streetNumber: "" }
 */
export function splitStreetAndCivico(combined: string): {
  street: string;
  streetNumber: string;
} {
  const trimmed = combined.trim();
  if (trimmed.length === 0) return { street: '', streetNumber: '' };
  const tokens = trimmed.split(/\s+/);
  if (tokens.length < 2) return { street: trimmed, streetNumber: '' };

  // Common Italian trailing modifiers — when the last token is one of
  // these AND the previous token starts with a digit, treat the pair
  // as a single civic ("12 bis", "8 ter", "5 interno").
  const TRAILING_MODIFIER = /^(bis|ter|quater|interno|int)\.?$/i;
  const lastToken = tokens[tokens.length - 1];
  const prevToken = tokens.length >= 2 ? tokens[tokens.length - 2] : undefined;
  if (
    TRAILING_MODIFIER.test(lastToken) &&
    prevToken !== undefined &&
    /^\d/.test(prevToken)
  ) {
    return {
      street: tokens.slice(0, -2).join(' '),
      streetNumber: `${prevToken} ${lastToken}`,
    };
  }

  if (/^\d/.test(lastToken)) {
    return {
      street: tokens.slice(0, -1).join(' '),
      streetNumber: lastToken,
    };
  }
  return { street: trimmed, streetNumber: '' };
}

/**
 * Search Italian addresses matching `query`. Returns normalized address
 * shapes ready to drop into a form.
 *
 * - Empty / very short queries (<3 chars) short-circuit to `[]` to avoid
 *   wasting session-token tier on unhelpful single-letter searches.
 * - Errors throw — the caller decides whether to show a toast / inline
 *   message / silent skip. AbortError is the caller's signal that a
 *   newer query has superseded this one.
 * - Hard limit: 6 results. The dropdown renders all of them; HERE
 *   ranking by relevance puts the best match first.
 */
export async function discoverAddress(
  query: string,
  apiKey: string,
  signal?: AbortSignal,
): Promise<NormalizedAddress[]> {
  if (!apiKey) {
    throw new Error('HERE Maps apiKey is required');
  }
  if (query.trim().length < 3) return [];

  const url = new URL(DISCOVER_ENDPOINT);
  url.searchParams.set('q', query);
  url.searchParams.set('at', ITALY_CENTROID);
  url.searchParams.set('in', 'countryCode:ITA');
  url.searchParams.set('limit', '6');
  url.searchParams.set('lang', 'it');
  url.searchParams.set('apiKey', apiKey);

  const res = await fetch(url, { signal });
  if (!res.ok) {
    throw new Error(`HERE Discover ${res.status} ${res.statusText}`);
  }
  const data = (await res.json()) as HereDiscoverResponse;
  return (data.items ?? []).map(itemToAddress).filter(isAddressLike);
}

function itemToAddress(item: HereDiscoverItem): NormalizedAddress {
  const a = item.address ?? {};
  const streetName = cleanString(a.street);
  const houseNumber = cleanString(a.houseNumber);
  const street =
    [streetName, houseNumber].filter((v): v is string => Boolean(v)).join(' ') ||
    undefined;

  return {
    street,
    houseNumber,
    city: cleanString(a.city),
    postalCode: cleanString(a.postalCode),
    province: normalizeProvince(a),
    country: normalizeCountry(a.countryCode),
    label: cleanString(a.label) ?? item.title,
    id: item.id,
  };
}

/**
 * Filter out HERE results that lack the bare minimum we need for a
 * form (street + city + CAP). HERE Discover sometimes returns POIs
 * with partial address fields (`resultType: "place"`) — useful for
 * map use, useless for "indirizzo di residenza" forms.
 */
function isAddressLike(addr: NormalizedAddress): boolean {
  return Boolean(addr.street && addr.city && addr.postalCode);
}

/**
 * Trim + collapse empty/whitespace-only strings into `undefined`.
 * HERE inconsistently returns missing fields as either absent keys
 * OR empty strings — this helper hides that quirk.
 */
function cleanString(v: string | undefined | null): string | undefined {
  if (v == null) return undefined;
  const trimmed = v.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Resolve provincia to a 2-letter uppercase sigla.
 *
 *   1. Prefer `countyCode` when it's already a clean 2-letter sigla.
 *   2. Otherwise look up the full `county` name in the ISTAT-aligned
 *      table (case-insensitive). HERE for IT typically returns the
 *      official ISTAT name ("Roma", "Sud Sardegna", "Forlì-Cesena").
 *   3. If neither path resolves, return `undefined` — never guess.
 */
function normalizeProvince(
  a: NonNullable<HereDiscoverItem['address']>,
): string | undefined {
  const code = cleanString(a.countyCode);
  if (code && /^[A-Z]{2}$/i.test(code)) return code.toUpperCase();

  const county = cleanString(a.county);
  if (county) {
    const sigla = ITALIAN_PROVINCE_BY_NAME[county.toLowerCase()];
    if (sigla) return sigla;
  }

  return undefined;
}

/**
 * Resolve countryCode to ISO 3166-1 alpha-2 uppercase.
 *
 * HERE returns alpha-3 ("ITA"); we convert via the ISO3_TO_ISO2 table.
 * If HERE ever returns a clean alpha-2 directly we accept it as-is
 * (defensive). Anything else (numeric region code, full country name,
 * unknown alpha-3) is rejected as `undefined` — never leave a non-
 * normalised value to confuse downstream consumers.
 */
function normalizeCountry(countryCode: string | undefined): string | undefined {
  const cleaned = cleanString(countryCode);
  if (!cleaned) return undefined;
  if (/^[A-Z]{3}$/i.test(cleaned)) {
    return ISO3_TO_ISO2[cleaned.toUpperCase()];
  }
  if (/^[A-Z]{2}$/i.test(cleaned)) return cleaned.toUpperCase();
  return undefined;
}

/**
 * Italian province name → ISTAT 2-letter sigla. Lowercase keys for
 * case-insensitive lookup. Sourced from the official ISTAT province
 * list (107 entries — "Sud Sardegna" replaces the legacy
 * Carbonia-Iglesias / Medio Campidano / Olbia-Tempio / Ogliastra
 * groupings; we keep the legacy keys too as a safety net for HERE
 * datasets that haven't fully migrated).
 *
 * Add a row when you see HERE return a county name that's not here.
 */
const ITALIAN_PROVINCE_BY_NAME: Readonly<Record<string, string>> = {
  agrigento: 'AG',
  alessandria: 'AL',
  ancona: 'AN',
  aosta: 'AO',
  arezzo: 'AR',
  'ascoli piceno': 'AP',
  asti: 'AT',
  avellino: 'AV',
  bari: 'BA',
  'barletta-andria-trani': 'BT',
  belluno: 'BL',
  benevento: 'BN',
  bergamo: 'BG',
  biella: 'BI',
  bologna: 'BO',
  bolzano: 'BZ',
  brescia: 'BS',
  brindisi: 'BR',
  cagliari: 'CA',
  caltanissetta: 'CL',
  campobasso: 'CB',
  caserta: 'CE',
  catania: 'CT',
  catanzaro: 'CZ',
  chieti: 'CH',
  como: 'CO',
  cosenza: 'CS',
  cremona: 'CR',
  crotone: 'KR',
  cuneo: 'CN',
  enna: 'EN',
  fermo: 'FM',
  ferrara: 'FE',
  firenze: 'FI',
  foggia: 'FG',
  'forlì-cesena': 'FC',
  'forli-cesena': 'FC',
  frosinone: 'FR',
  genova: 'GE',
  gorizia: 'GO',
  grosseto: 'GR',
  imperia: 'IM',
  isernia: 'IS',
  'la spezia': 'SP',
  "l'aquila": 'AQ',
  latina: 'LT',
  lecce: 'LE',
  lecco: 'LC',
  livorno: 'LI',
  lodi: 'LO',
  lucca: 'LU',
  macerata: 'MC',
  mantova: 'MN',
  'massa-carrara': 'MS',
  'massa carrara': 'MS',
  matera: 'MT',
  messina: 'ME',
  milano: 'MI',
  modena: 'MO',
  'monza e della brianza': 'MB',
  'monza-brianza': 'MB',
  napoli: 'NA',
  novara: 'NO',
  nuoro: 'NU',
  oristano: 'OR',
  padova: 'PD',
  palermo: 'PA',
  parma: 'PR',
  pavia: 'PV',
  perugia: 'PG',
  'pesaro e urbino': 'PU',
  'pesaro-urbino': 'PU',
  pescara: 'PE',
  piacenza: 'PC',
  pisa: 'PI',
  pistoia: 'PT',
  pordenone: 'PN',
  potenza: 'PZ',
  prato: 'PO',
  ragusa: 'RG',
  ravenna: 'RA',
  'reggio calabria': 'RC',
  'reggio emilia': 'RE',
  "reggio nell'emilia": 'RE',
  rieti: 'RI',
  rimini: 'RN',
  roma: 'RM',
  rovigo: 'RO',
  salerno: 'SA',
  sassari: 'SS',
  savona: 'SV',
  siena: 'SI',
  siracusa: 'SR',
  sondrio: 'SO',
  'sud sardegna': 'SU',
  taranto: 'TA',
  teramo: 'TE',
  terni: 'TR',
  torino: 'TO',
  trapani: 'TP',
  trento: 'TN',
  treviso: 'TV',
  trieste: 'TS',
  udine: 'UD',
  varese: 'VA',
  venezia: 'VE',
  'verbano-cusio-ossola': 'VB',
  vercelli: 'VC',
  verona: 'VR',
  'vibo valentia': 'VV',
  vicenza: 'VI',
  viterbo: 'VT',
  // Legacy ISTAT codes (some HERE datasets still surface these);
  // they all map to "Sud Sardegna" as of the 2016 reform.
  'carbonia-iglesias': 'SU',
  'medio campidano': 'SU',
  'olbia-tempio': 'SU',
  ogliastra: 'SU',
};

/**
 * ISO 3166-1 alpha-3 → alpha-2. HERE returns alpha-3; the rest of
 * the app uses alpha-2. Limited to EU + EEA + UK + Switzerland +
 * common third-country partners — extend when we lift the
 * `countryCode:ITA` filter in `discoverAddress`. Anything not in
 * this table resolves to `undefined` (never leave a non-normalised
 * code on the consumer side).
 */
const ISO3_TO_ISO2: Readonly<Record<string, string>> = {
  ITA: 'IT',
  // EU + EEA + UK + CH
  AUT: 'AT',
  BEL: 'BE',
  BGR: 'BG',
  HRV: 'HR',
  CYP: 'CY',
  CZE: 'CZ',
  DNK: 'DK',
  EST: 'EE',
  FIN: 'FI',
  FRA: 'FR',
  DEU: 'DE',
  GRC: 'GR',
  HUN: 'HU',
  IRL: 'IE',
  LVA: 'LV',
  LTU: 'LT',
  LUX: 'LU',
  MLT: 'MT',
  NLD: 'NL',
  POL: 'PL',
  PRT: 'PT',
  ROU: 'RO',
  SVK: 'SK',
  SVN: 'SI',
  ESP: 'ES',
  SWE: 'SE',
  ISL: 'IS',
  LIE: 'LI',
  NOR: 'NO',
  CHE: 'CH',
  GBR: 'GB',
  // Microstates often referenced on Italian docs
  SMR: 'SM',
  VAT: 'VA',
  MCO: 'MC',
  AND: 'AD',
  // Common non-EU partners
  USA: 'US',
  CAN: 'CA',
  MEX: 'MX',
  BRA: 'BR',
  ARG: 'AR',
  CHN: 'CN',
  JPN: 'JP',
  KOR: 'KR',
  IND: 'IN',
  AUS: 'AU',
  NZL: 'NZ',
  RUS: 'RU',
  TUR: 'TR',
  ALB: 'AL',
  SRB: 'RS',
  MNE: 'ME',
  BIH: 'BA',
  MKD: 'MK',
  UKR: 'UA',
};
